import { Injectable } from '@angular/core';
import { EdlCommonModalService } from '@edl-development/edl-common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ObservationCommentControllerModal } from '../components/observation-comments/components/observation-comment-controller-modal/observation-comment-controller-modal.component';
import { FileAttachment } from '../model/attachment/file-attachment.model';
import { BaseUserRoleDTO } from '../model/base/base-user-role-dto.model';
import { EdlComment } from '../model/comment.model';
import { DateTimeDTO } from '../model/date-time-dto.model';
import { ObservationTypeValues } from '../model/enum/observation-type-values.enum';
import { EEARType } from '../model/enum/reporting/eear-type.enum';
import { ObservationSummaryDTO } from '../model/observation/observation-summary-dto.model';
import { UserSummaryPermissions } from '../model/observation/user-summary-permisions.model';
import { CreateObservationComponent } from '../observations/create-observation/create-observation.component';
import { EearInfoModalComponent } from '../reporting/eear-report/components/eear-info-modal/eear-info-modal.component';
import { MessagingService } from '../services/messaging/messaging.service';
import { GenericType } from './../model/base/edl-generic-type.model';
import { AuthenticateModalComponent } from './authenitcate-modal/authenticate-modal.component';
import { EdlGenericModalComponent } from './edl-generic-modal/edl-generic-modal.component';
import { EdlModalDatePickerComponent } from './edl-modal-date-picker/edl-modal-date-picker.component';
import { EdlSendNotificationModal } from './edl-send-notification-modal/edl-send-notification-modal.component';
import { FileAttachmentModalComponent } from './file-attachment-modal/file-attachment-modal.component';
import { PaetepDuplicateObsModalComponent } from './paetep-duplicate-obs-modal/paetep-duplicate-obs-modal.component';
import { SignatureModalComponent } from './signature-modal/signature-modal.component';
import { ReloadApplicationModalComponent } from './reload-app-modal/reload-app-modal.component';

@Injectable()
export class ModalService extends EdlCommonModalService {
    constructor(
        protected override ngbModal: NgbModal,
        private messagingService: MessagingService,
        private toastr: ToastrService
    ) {
        super(ngbModal);
    }

    openCommentController(
        comment: EdlComment | null,
        observationType?: GenericType,
        observationId?: string,
        lookupValue?: string,
        observationSummary?: ObservationSummaryDTO,
        hideLookup?: boolean) {
        this.currentModal = this.ngbModal.open(ObservationCommentControllerModal, {
            size: 'lg',
            windowClass: 'comment-controller-modal',
            backdrop: 'static'
        });

        this.currentModal.componentInstance.comment = comment;
        this.currentModal.componentInstance.active = 1;
        this.currentModal.componentInstance.observationType = observationType;
        this.currentModal.componentInstance.observationId = observationId;
        this.currentModal.componentInstance.defaultLookupValue = lookupValue;
        this.currentModal.componentInstance.observationSummary = observationSummary;
        this.currentModal.componentInstance.hideLookup = hideLookup;

        return this.currentModal.result;
    }

    openCreateObservationController(
        currentUserSummaryTab: number,
        targetUserId: string,
        targetUserName: string,
        currentUser: string,
        targetUserRoles: BaseUserRoleDTO[] = [],
        userSummaryPermissions?: UserSummaryPermissions
    ) {
        this.currentModal = this.ngbModal.open(CreateObservationComponent, {
            size: 'lg',
            windowClass: 'comment-controller-modal',
            backdrop: 'static',
        });

        this.currentModal.componentInstance.currentUserSummaryTab = currentUserSummaryTab;
        this.currentModal.componentInstance.targetUserId = targetUserId;
        this.currentModal.componentInstance.targetUserName = targetUserName;
        this.currentModal.componentInstance.targetUserRoles = targetUserRoles;
        this.currentModal.componentInstance.evalUserId = currentUser;
        this.currentModal.componentInstance.userSummaryPermissions = userSummaryPermissions;

        return this.currentModal.result;
    }

    openDatePickerController(title: string, label: string, dateTime?: DateTimeDTO): Promise<DateTimeDTO> {
        this.currentModal = this.ngbModal.open(EdlModalDatePickerComponent, {
            size: 'md',
            windowClass: 'date-picker-modal',
            backdrop: 'static'
        });
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.label = label;
        this.currentModal.componentInstance.dateTime = dateTime;

        return this.currentModal.result;
    }

    openAuthenticateModal(title: string, label: string) {
        this.currentModal = this.ngbModal.open(AuthenticateModalComponent, {
            size: 'md',
            windowClass: 'authenticate-modal',
            backdrop: 'static'
        });
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.label = label;

        return this.currentModal.result;
    }

    openFileAttachmentsModal(openUpload: boolean, attachments: FileAttachment[], observationSummary: ObservationSummaryDTO, attachmentLabel: string, comment?: EdlComment, isReadOnly: boolean = false) {

        this.currentModal = this.ngbModal.open(FileAttachmentModalComponent, {
            size: 'lg',
            windowClass: 'attchments-modal',
            backdrop: 'static'
        });

        this.currentModal.componentInstance.observationFiles = attachments;
        this.currentModal.componentInstance.attachmentLabel = attachmentLabel;
        this.currentModal.componentInstance.observationSummary = observationSummary;
        this.currentModal.componentInstance.tabIndex = openUpload ? 2 : 1;
        this.currentModal.componentInstance.comment = comment;
        this.currentModal.componentInstance.isReadOnly = isReadOnly;

        return this.currentModal.result;
    }



    openSignatureModal(title: string, label: string) {
        this.currentModal = this.ngbModal.open(SignatureModalComponent, {
            size: 'lg',
            windowClass: 'signature-modal',
            backdrop: 'static'
        });
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.label = label;

        return this.currentModal.result;
    }

    openGenericModal(title: string, message: string, confirmLabel: string, cancelLabel: string) {
        this.currentModal = this.ngbModal.open(EdlGenericModalComponent, { backdrop: 'static' });
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.message = message;
        this.currentModal.componentInstance.confirmLabel = confirmLabel;
        this.currentModal.componentInstance.cancelLabel = cancelLabel;

        return this.currentModal.result;
    }

    openSendNotificationModal(title: string, message: string, yesButtonText?: string, noButtonText?: string) {
        this.currentModal = this.ngbModal.open(EdlSendNotificationModal, { size: 'lg', backdrop: 'static' });
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.message = message;
        this.currentModal.componentInstance.yesButtonText = yesButtonText;
        this.currentModal.componentInstance.noButtonText = noButtonText;

        return this.currentModal.result;
    }

    openSendObservationNudge(targetUserId: string, observationId: string, observationType: ObservationTypeValues, observationUserId: string) {
        return this.openSendNotificationModal('Nudge', 'Would you like to send a nudge?<br /><br />Enter additional message:')
            .then((message: string) => firstValueFrom(
                this.messagingService.nudgeObservation({
                    targetUserId,
                    observationId,
                    observationType,
                    observationUserId,
                    message
                })
            )).then(() => this.toastr.success("User Nudged", "Nudge"));
    }

    openDuplicateObsModal(
        observationType: ObservationTypeValues,
        observationId: string,
        targetUserId: string,
        userSummaryPermissions: UserSummaryPermissions
    ) {
        this.currentModal = this.ngbModal.open(PaetepDuplicateObsModalComponent, { backdrop: 'static' });
        this.currentModal.componentInstance.observationType = observationType;
        this.currentModal.componentInstance.observationId = observationId;
        this.currentModal.componentInstance.targetUserId = targetUserId;
        this.currentModal.componentInstance.userSummaryPermissions = userSummaryPermissions;

        return this.currentModal.result;
    }

    openEEARInfoModal(eearType: EEARType) {
        this.currentModal = this.ngbModal.open(EearInfoModalComponent, {
            size: 'lg',
            backdrop: 'static'
        });
        this.currentModal.componentInstance.eearType = eearType;

        return this.currentModal.result;
    }

    openApplicationReloadModal() {
      this.currentModal = this.ngbModal.open(ReloadApplicationModalComponent, {
          size: 'md',
      });

      return this.currentModal.result;
  }
}
